export interface IPermissionCatalogItem {
  id: number;
  name: string;
}

const PERMISSION_CATALOG_STORAGE_KEY = "permissionCatalog";

/** Normalize permission ids from API (handles string numbers). */
export const normalizePermissionIds = (ids: unknown[] = []): number[] =>
  ids
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);

/** Find permission id by exact seeder name, e.g. `Role_List`. */
export const findPermissionIdByName = (
  catalog: IPermissionCatalogItem[],
  permissionName: string
): number | null => {
  const match = catalog.find((item) => item.name === permissionName);
  if (!match) return null;
  const id = Number(match.id);
  return Number.isFinite(id) ? id : null;
};

/**
 * Check access: find permission by name in catalog → get id → user has that id.
 */
export const userHasPermissionByName = (
  permissionName: string,
  catalog: IPermissionCatalogItem[],
  userPermissionIds: number[]
): boolean => {
  if (!permissionName || !catalog.length) return false;

  const permissionId = findPermissionIdByName(catalog, permissionName);
  if (permissionId == null) return false;

  const userIdSet = new Set(normalizePermissionIds(userPermissionIds));
  return userIdSet.has(permissionId);
};

/** Resolve user permission ids against the public catalog. */
export const matchUserPermissions = (
  catalog: IPermissionCatalogItem[],
  permissionIds: number[]
): IPermissionCatalogItem[] => {
  const normalizedIds = normalizePermissionIds(permissionIds);
  if (!catalog.length || !normalizedIds.length) return [];

  const idSet = new Set(normalizedIds);
  return catalog.filter((item) => idSet.has(Number(item.id)));
};

export const readStoredPermissionCatalog = (): IPermissionCatalogItem[] => {
  try {
    const raw = localStorage.getItem(PERMISSION_CATALOG_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const storePermissionCatalog = (
  catalog: IPermissionCatalogItem[]
): void => {
  localStorage.setItem(
    PERMISSION_CATALOG_STORAGE_KEY,
    JSON.stringify(catalog)
  );
};
